<template>
  <Teleport to="body">
    <div
      v-if="toastStore.hasToasts"
      ref="containerEl"
      class="toast-container"
      :class="positionClasses"
      :data-toast-position="position"
      aria-live="polite"
      aria-atomic="false"
      aria-relevant="additions"
    >
      <TransitionGroup
        name="toast-list"
        tag="div"
        class="space-y-4"
      >
        <Toast
          v-for="toast in visibleToasts"
          :key="toast.id"
          :toast="toast"
          @dismiss="toastStore.removeToast"
        />
      </TransitionGroup>

      <!-- +N more badge -->
      <div
        v-if="hiddenCount > 0"
        class="toast-more-badge"
        role="status"
        aria-label="Más notificaciones disponibles"
      >
        +{{ hiddenCount }} más
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useToastStore } from '@/stores/toast'
import Toast from './AppToast.vue'

type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'

interface Props {
  position?: ToastPosition
  maxToasts?: number
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top-right',
  maxToasts: 5
})

const toastStore = useToastStore()

const positionClasses = computed(() => {
  const map: Record<ToastPosition, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  }
  return map[props.position]
})

const visibleToasts = computed(() => {
  return toastStore.activeToasts.slice(0, props.maxToasts)
})

const hiddenCount = computed(() => {
  return Math.max(0, toastStore.activeToasts.length - props.maxToasts)
})

/**
 * Anclaje vertical en ESCRITORIO (c717).
 *
 * En escritorio el contenedor usaba el `top-4` de `positionClasses` (y=16) y,
 * como está en z-50 por encima de la cabecera, BARRABA los controles de arriba
 * a la derecha: medido vivo a 1440×900 con un toast en pantalla, 4 de 6
 * controles de la cabecera quedaban intocables (Ayuda, Configuración,
 * Notificaciones y el menú de UX Admin — `toast.contains(elementFromPoint(c))`).
 *
 * El alto de la cabecera NO es una constante: 77px a 1440/1024/700/641 y 85px a
 * 768, donde el contenido envuelve. Por eso no va un número mágico: se MIDE el
 * borde inferior de `[data-app-header]` y se publica en `--toast-top`, que la
 * regla de escritorio consume. Un `ResizeObserver` re-mide cuando la cabecera
 * cambia de alto.
 *
 * Se publica la VARIABLE, nunca `style.top`: la propiedad `top` inline ganaría al
 * `top-auto` del MQ móvil de c716 y dejaría el snackbar del teléfono pegado
 * arriba, tapando la cabecera otra vez.
 */
const containerEl = ref<HTMLElement | null>(null)

/** Hueco en px entre el borde inferior de la cabecera y el toast. */
const HEADER_GAP = 12

let resizeObserver: ResizeObserver | null = null

/** La cabecera de la aplicación — la que declara el contrato `data-app-header`. */
function appHeader(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-app-header]')
}

function syncTopOffset(): void {
  const el = containerEl.value
  if (!el) return
  const header = appHeader()
  // Sin cabecera declarada (otro layout) se deja el valor por defecto de la regla.
  if (!header) return
  const bottom = Math.round(header.getBoundingClientRect().bottom)
  el.style.setProperty('--toast-top', `${bottom + HEADER_GAP}px`)
}

watch(
  containerEl,
  (el, prev) => {
    if (prev) {
      resizeObserver?.disconnect()
      resizeObserver = null
    }
    // `bottom-*` ya viene anclado abajo: darle `top` haría ESTIRAR la caja
    // entre top y bottom (CSS 2.1 §10.6.4), no anclarla.
    if (!el || !props.position.startsWith('top')) return
    syncTopOffset()
    // jsdom no tiene ResizeObserver; guardas por eso, no sólo por costumbre.
    if (typeof ResizeObserver === 'undefined') return
    const header = appHeader()
    if (!header) return
    resizeObserver = new ResizeObserver(syncTopOffset)
    resizeObserver.observe(header)
  },
  { flush: 'post' }
)

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<style scoped>
/* c733 — `z-[1000]`, no `z-50`: el toast responde a una acción que se acaba de
   hacer DENTRO de un overlay, así que tiene que ganarle a TODOS.

   MEDIDO en vivo a 390×844 con el modal "Crear nuevo usuario" abierto: el toast
   estaba en z-50, el envolturo del modal en z-50, y
   `document.elementFromPoint` en el centro del snackbar devolvía el botón
   "Cancelar" del modal. El toast existía y era `visible`, pero en un teléfono
   el usuario que mandaba un correo repetido no veía NADA.

   Con empate de nivel gana el ÚLTIMO en el árbol (CSS 2.1 §9.9.1), y los
   modales se teletransportan a <body> al abrirse, o sea después del contenedor
   del toast, que se monta con el layout. El modal ganaba siempre. En escritorio
   el defecto sólo no se veía por geometría (el toast va arriba a la derecha y
   el modal es `max-w-2xl` centrado) — ninguna guarda lo impedía.

   Por qué 1000 y no renumerar la capa de overlays: `z-50` es de facto la capa
   de modal de la app (36 archivos) y `GlobalSearch.vue` ya llega a `z-[100]`.
   Renumerar eso es un cambio de riesgo masivo que no compra nada; se sube sólo
   el toast, con 10× de holgura sobre el máximo actual para que un overlay nuevo
   no vuelva a empatar. `ToastAboveOverlays.spec.ts` falla si algún día el
   nivel de cualquier otro elemento alcanza al del toast. */
.toast-container {
  @apply fixed z-[1000] pointer-events-none;
  max-width: 24rem;
}

.toast-more-badge {
  @apply mt-2 px-3 py-1.5 rounded-full
         bg-neutral-900/90 dark:bg-neutral-100/90
         text-white dark:text-neutral-900
         text-xs font-medium
         pointer-events-auto
         text-center;
}

/* List transitions */
.toast-list-move,
.toast-list-enter-active,
.toast-list-leave-active {
  transition: all 0.3s ease;
}

.toast-list-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.toast-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.toast-list-leave-active {
  position: absolute;
  right: 0;
}

/* Desktop: clear the app header (c717).
   `top-4` (y=16) queda DENTRO de la cabecera de 77px, y como el contenedor es
   z-50 opaco, tapaba 4 de sus 6 controles mientras hubiera un toast visible.
   El desplazamiento real lo publica el runtime midiendo `[data-app-header]`;
   el `rem` de aquí es sólo el valor por defecto para antes del primer pintado.
   El selector por atributo deja fuera a `bottom-*`: darle `top` a una caja que
   ya trae `bottom` la estiraría. Arranca en 640.02 porque el MQ móvil de abajo
   es `max-width: 640px` INCLUSIVO — 640 exacto quedaría sin cubrir. */
@media (min-width: 640.02px) {
  .toast-container[data-toast-position^='top'] {
    top: var(--toast-top, 5.75rem);
  }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  /* Anclar ABAJO, no sólo a lo ancho. Antes este bloque conservaba el `top-4`
     de `positionClasses` y el toast se convertía en un banner a lo ancho
     pegado arriba que tapaba los 5 controles del header (menú, búsqueda,
     ajustes, notificaciones y perfil) durante toda su vida. En móvil el toast
     va abajo, como un snackbar; en desktop la posición superior se conserva. */
  .toast-container {
    @apply left-4 right-4 top-auto bottom-4;
    max-width: none;
  }
}
</style>
